// noinspection JSCheckFunctionSignatures,JSVoidFunctionReturnValueUsed

import DefaultGameParams from '@/classes/DefaultGameParams';
import ExtendedGameParams from '@/classes/ExtendedGameParams';
import getIdToken from '@/helpers/firebase-token';
import { BANK_IDS } from '@/store';

/**

 * This mixin is a reusable piece of code in Vue.js
 * Provides functionalities related to handling clickable images in the application.
 * It includes computed properties and methods that are used to manage the behavior of clickable images
 * based on the user's authorization status and the player mode.

 */

const DEFAULT_GAMES = ['FF', 'TMB'];

const PARAMS_CLASSES = {
  default: DefaultGameParams,
  extended: ExtendedGameParams,
  // Add more mappings as needed
};
const gameIdToUrlMapper = {
  1: process.env.VUE_APP_MULTIPLINKO_GAME_URL,
  2: process.env.VUE_APP_ROLL_OR_HOLD_CAF_URL,
  5: process.env.VUE_APP_ROLLING_21_CAF_URL,
  6: process.env.VUE_APP_MULTIMINES_CAF_URL,
  10: process.env.VUE_APP_ESCAPE_THE_CIRCLE_GAME_URL,
  12: process.env.VUE_APP_SCARAB_PLINKO_GAME_URL,
  14: process.env.VUE_APP_MAX_BLOCKS_GAME_URL,
  15: process.env.VUE_APP_MAX_PLOSION_GAME_URL,
  16: process.env.VUE_APP_DUEL_21_GAME_URL,
  18: process.env.VUE_APP_ESCAPE_THE_CIRCLE_XTREME_GAME_URL,
};

export const GameLauncher = {
  data: () => ({
    redirectToCAF: false,
    specificGameURLs: {
      'olympus-limited-access': process.env.VUE_APP_PLINKO_GAME_URL,
      'multiplinko-limited-access': process.env.VUE_APP_MULTIPLINKO_GAME_URL,
      'roll-or-hold-limited-access': process.env.VUE_APP_ROLL_OR_HOLD_GAME_URL,
      'escape-the-circle-limited-access': process.env.VUE_APP_ESCAPE_THE_CIRCLE_GAME_URL,
      multiplinko: process.env.VUE_APP_MULTIPLINKO_GAME_URL,
      plinko: process.env.VUE_APP_PLINKO_GAME_URL,
      'roll-or-hold': process.env.VUE_APP_ROLL_OR_HOLD_GAME_URL,
      'rolling-21': process.env.VUE_APP_ROLLING_21_GAME_URL,
      multimines: process.env.VUE_APP_MULTIMINES_GAME_URL,
      fastmines: process.env.VUE_APP_FASTMINES_GAME_URL,
      'max-21': process.env.VUE_APP_MAX_21_GAME_URL,
      'max-dice': process.env.VUE_APP_MAX_DICE_GAME_URL,
      'escape-the-circle': process.env.VUE_APP_ESCAPE_THE_CIRCLE_GAME_URL,
      'scarab-plinko': process.env.VUE_APP_SCARAB_PLINKO_GAME_URL,
      'max-blocks': process.env.VUE_APP_MAX_BLOCKS_GAME_URL,
      maxplosion: process.env.VUE_APP_MAX_PLOSION_GAME_URL,
      'duel-21': process.env.VUE_APP_DUEL_21_GAME_URL,
      'escape-the-circle-xtreme': process.env.VUE_APP_ESCAPE_THE_CIRCLE_XTREME_GAME_URL,
    },
    historyRecordAdded: false,
    SIDListener: null,
    messageListener: null,
  }),

  computed: {
    QCMode() {
      return this.$store.getters.isQCMode;
    },

    userDetails() {
      return this.$store.state.user;
    },

    isAuthorized() {
      return !!this.userDetails;
    },

    userBalance() {
      return this.$store.getters.getUserTotalBalance.balance;
    },
  },

  beforeDestroy() {
    this.unsubscribeBrowserHistory();
    this.removeMessageListeners();
  },

  methods: {
    subscribeBrowserHistory() {
      const pageState = { page: 'game' };

      // If history is empty then push the state to enable back button
      // (length = 1 means there is no history, only one active page)
      if (window.history?.length < 2 || window.history.state !== pageState) {
        window.history.pushState(pageState, null, window.location.href);
        this.historyRecordAdded = true;
      }

      window.addEventListener('popstate', this.handleBrowserBackButton);
    },

    unsubscribeBrowserHistory(causedByBackButton = false) {
      window.removeEventListener('popstate', this.handleBrowserBackButton);

      if (causedByBackButton || !this.historyRecordAdded) return;
      this.historyRecordAdded = false;
      window.history?.back();
    },

    getRedirectUrl(url) {
      if (this.isAuthorized && url) {
        return url;
      }

      return '';
    },

    async handleBrowserBackButton() {
      await this.closeGame({ causedByBackButton: true });
    },

    getGameUrl() {
      // PVP games special urls
      if (this.roomsConfig && this.gameType) {
        switch (this.gameType) {
          case 'multi-mines':
            return this.specificGameURLs.multimines;
          case 'plinko':
            return this.specificGameURLs.multiplinko;
          case 'escape-the-circle-xtreme':
            return this.specificGameURLs['escape-the-circle-xtreme'];
        }
      }

      for (const game in this.specificGameURLs) {
        if (window.location.href.includes(game)) {
          return this.specificGameURLs[game];
        }
      }

      return null;
    },

    /**
     * Constructs a Unity game URL based on the provided parameters.
     *
     * @param {object} options - An object containing the game construction parameters.
     * @param {string} options.gameid - The ID of the game.
     * @param {string} options.bankId - The ID of the bank.
     * @param {string} options.token - The token for the game.
     * @param {boolean} options.battlegroundGame - Whether the game is a battleground game.
     * @param {string} options.paramsType - The type of parameters to use.
     * @param {string} options.buyIn - The buy-in amount for the game.
     * @param {string} options.addFriendUrl - The URL for adding friends.
     * @return {string} The constructed Unity game URL.
     */
    async constructUnityGameUrl(options) {
      const { gameid, bankId, token, battlegroundGame, paramsType, buyIn, addFriendUrl } = options;
      const url = new URL(gameIdToUrlMapper[gameid]);
      const displayName = this.userDetails.displayName;
      const idToken = await getIdToken({ forceLoad: true });
      const ParamsClass = PARAMS_CLASSES[paramsType];

      if (!ParamsClass) {
        throw new Error(`Invalid paramsType: ${paramsType}`);
      }

      const params = new URLSearchParams(
        new ParamsClass(gameid, bankId, token, process.env.VUE_APP_CHALLENGE_URL, displayName, idToken, addFriendUrl),
      );

      if (battlegroundGame) {
        params.append('buyIn', buyIn);
      }

      url.search = params.toString();

      return url.toString();
    },

    getSpecificGameURL(gameId, defaultUrl) {
      switch (gameId) {
        case 2:
          return new URL(this.specificGameURLs['roll-or-hold']);
        case 5:
          return new URL(this.specificGameURLs['rolling-21']);
        case 7:
          return new URL(this.newGames['max-dice']);
        case 8:
          return new URL(this.specificGameURLs.fastmines);
        case 9:
          return new URL(this.newGames['max-21']);
        case 10:
          return new URL(this.specificGameURLs['escape-the-circle']);
        case 12:
          return new URL(this.specificGameURLs['scarab-plinko']);
        case 14:
          return new URL(this.specificGameURLs['max-blocks']);
        case 15:
          return new URL(this.specificGameURLs['maxplosion']);
        case 16:
          return new URL(this.specificGameURLs['duel-21']);
        default:
          return defaultUrl;
      }
    },

    /**
     * Constructs the game URL based on the provided parameters.
     *
     * @param {object} options - An object containing the game parameters.
     * @param {string} options.gameid - The ID of the game.
     * @param {string} options.bankId - The ID of the bank.
     * @param {string} options.homeUrl - The home URL.
     * @param {string} options.token - The token for the game.
     * @param {boolean} options.battlegroundGame - Whether the game is a battleground game.
     * @param {string} options.paramsType - The type of parameters to use.
     * @param {string} options.addFriendUrl - The URL for adding friends.
     * @return {string} The constructed game URL.
     */
    async constructGameURL(options) {
      const { gameid, bankId, homeUrl, homeUrlId, token, battlegroundGame, paramsType, addFriendUrl } = options;
      const mqGameUrl = await this.$store.dispatch('getMqGameDomain');
      const gamesUrl = battlegroundGame
        ? process.env.VUE_APP_BATTLEGROUND_GAMES_URL
        : process.env.VUE_APP_DEFAULT_GAMES_URL;
      const defaultUrl = new URL(`${mqGameUrl}/${gamesUrl}`);

      let url;

      if (this.getGameUrl()) {
        url = new URL(this.getGameUrl());
      } else if (homeUrlId === 'PL') {
        // TODO: remove this hack later
        url = new URL(this.specificGameURLs.plinko);
      } else {
        url = this.getSpecificGameURL(parseInt(gameid, 10), defaultUrl);
      }

      // displayName and idToken are required to use for the new games (f.e. Multiplinko)
      const displayName = this.userDetails.displayName;
      const idToken = await getIdToken({ forceLoad: true });
      const ParamsClass = PARAMS_CLASSES[paramsType];

      if (!ParamsClass) {
        throw new Error(`Invalid paramsType: ${paramsType}`);
      }

      const params = new URLSearchParams(
        new ParamsClass(gameid, bankId, token, homeUrl, displayName, idToken, addFriendUrl),
      );

      if (battlegroundGame) {
        // check for MBC stake
        if (parseInt(gameid, 10) === 3) {
          params.append('buyIn', undefined);
        } else {
          params.append('buyIn', this.QCMode ? this.selectedQCStake : this.selectedMCStake);
        }
      }

      url.search = params.toString();

      return url.toString();
    },

    openSignUpDialog() {
      if (!this.isAuthorized) {
        this.$store.dispatch('changeSignupDialogState', true);
      }
    },

    async relaunchGame(gameId, gameURL) {
      const { getters, dispatch } = this.$store;

      if (!this.isAuthorized) {
        this.openSignUpDialog();
        return;
      }

      if (!gameURL || !gameId || !this.checkUserBalance()) {
        return;
      }

      await dispatch('requestGameplayTokenAs');

      const token = getters.getGameplayToken;
      const decodedUrl = decodeURIComponent(gameURL);
      const urlObj = new URL(decodedUrl);
      const battlegroundUrl = process.env.VUE_APP_BATTLEGROUND_PRIVATE_GAMES_URL;

      if (battlegroundUrl && decodedUrl.includes(battlegroundUrl)) {
        urlObj.searchParams.append('mqcToken', token);
        urlObj.searchParams.append('mmcToken', token);
      } else {
        urlObj.searchParams.append('token', token);
      }

      const generatedGameUrl = urlObj.toString();
      this.gamedetail = gameId;
      await this.updateIframeUrl(generatedGameUrl);
    },

    async launchGame(gameid, homeUrlId, paramsType = 'default', buyInAmount = null) {
      const { getters, dispatch } = this.$store;
      if (!this.isAuthorized) {
        this.openSignUpDialog();
        return;
      }

      if (parseInt(gameid, 10) === 875) {
        this.selectedQCStake = 200000;
        this.selectedMCStake = 100;
      }

      if (parseInt(gameid, 10) === 3) {
        this.selectedQCStake = 25000;
        this.selectedMCStake = 50;
      }

      if (typeof buyInAmount === 'number') {
        if (this.QCMode) this.selectedQCStake = buyInAmount;
        else this.selectedMCStake = buyInAmount;
      }

      if (!gameid || !this.checkUserBalance()) {
        return;
      }

      await dispatch('requestGameplayTokenAs');
      this.token = getters.getGameplayToken;
      await dispatch('changeHomeUrl', homeUrlId);

      const homeUrl = getters.getHomeUrl;
      const bankId = this.QCMode ? BANK_IDS.MQC : BANK_IDS.MMC;
      const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;
      this.gameURL = await this.constructGameURL({
        gameid,
        bankId,
        homeUrl,
        homeUrlId,
        battlegroundGame: !DEFAULT_GAMES.includes(homeUrlId),
        token: this.token,
        paramsType,
        addFriendUrl,
      });

      this.gamedetail = gameid;

      this.subscribeBrowserHistory();

      await this.showIFrameDialog(this.gameURL);
      this.pushStartGameEvent(gameid);

      let mqGameUrl = await this.$store.dispatch('getMqGameDomain');
      if (this.SIDListener == null) {
        this.addSIDListener(mqGameUrl);
      }
      this.postSIDMessage(mqGameUrl);
    },

    async launchGame2(gameid, homeURL, paramsType = 'default', buyIn) {
      const { getters, dispatch } = this.$store;

      if (!this.isAuthorized) {
        this.openSignUpDialog();

        return;
      }

      if (!gameid || !this.checkUserBalance()) {
        return;
      }

      await dispatch('requestGameplayTokenAs');
      this.token = getters.getGameplayToken;
      await dispatch('changeHomeUrl', homeURL);

      const homeUrl = getters.getHomeUrl;
      const bankId = this.QCMode ? BANK_IDS.MQC : BANK_IDS.MMC;
      const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;
      this.selectedMCStake = buyIn;

      this.gameURL = this.constructGameURL({
        gameid,
        bankId,
        homeUrl,
        battlegroundGame: !DEFAULT_GAMES.includes(homeURL),
        token: this.token,
        paramsType,
        addFriendUrl,
      });

      this.gamedetail = gameid;

      await this.showIFrameDialog(this.gameURL);
      this.pushStartGameEvent(gameid);
    },

    async launchGameUnity(
      gameid,
      homeURL,
      addFriendUrl = '',
      paramsType = 'default',
      buyIn,
      createPrivateRoom = false,
      privateRoomHostId = '',
      privateRoomId = '',
      coin = null,
    ) {
      const { getters, dispatch } = this.$store;

      if (!this.isAuthorized) {
        this.openSignUpDialog();
        return;
      }

      if (!gameid || !this.checkUserBalance()) {
        return;
      }

      await dispatch('requestGameplayTokenAs');
      this.token = getters.getGameplayToken;

      if (!this.token) {
        throw new Error('Error in creating game token');
      }

      await dispatch('changeHomeUrl', homeURL);
      const homeUrl = getters.getHomeUrl;
      let bankId = this.QCMode ? BANK_IDS.MQC : BANK_IDS.MMC;

      if (coin) {
        bankId = coin === 'QC' || coin === BANK_IDS.MQC ? BANK_IDS.MQC : BANK_IDS.MMC;
      }

      this.gameURL = await this.constructUnityGameUrl({
        gameid,
        bankId,
        homeUrl,
        battlegroundGame: !DEFAULT_GAMES.includes(homeURL),
        token: this.token,
        paramsType,
        buyIn,
        addFriendUrl: addFriendUrl,
      });

      this.gamedetail = gameid;

      this.gameURL = this.gameURL.replace('?', '?createPrivateRoom=' + createPrivateRoom + '&');

      if (privateRoomHostId) {
        this.gameURL = this.gameURL.replace('?', '?privateRoomHostId=' + privateRoomHostId + '&');
      }

      if (privateRoomId) {
        this.gameURL = this.gameURL.replace('?', '?privateRoomId=' + privateRoomId + '&');
      }

      const currentUser = getters.user;
      await dispatch('setLoadingClient', { userId: currentUser.id, gameId: gameid });
      await this.showIFrameDialog(this.gameURL);
      this.pushStartGameEvent(gameid);
    },

    pushStartGameEvent(gameId) {
      this.$store.dispatch('sendGaEvent', {
        endpoint: '/viewGame',
        body: {
          game_name: this.$store.getters.getGameById(gameId)?.NAME || gameId,
          currency: this.QCMode ? 'QC' : 'MC',
        },
      });
    },

    checkUserBalance(value = true) {
      const balance = this.userBalance;
      const hasBalance = this.QCMode ? balance.MQC > 0 : balance.MMC > 0;

      const isBalanceSufficient = this.QCMode
        ? balance.MQC >= (this.selectedQCStake?.value || this.selectedQCStake || 0)
        : balance.MMC >= (this.selectedMCStake?.value || this.selectedMCStake || 0);

      if (!hasBalance || !isBalanceSufficient) {
        this.$store.dispatch('showConfirmDialog', {
          title: 'Insufficient balance to play.',
          text: 'Please visit cashier in order to deposit to your balance.',
          yesText: 'Deposit Now',
          noText: 'Dismiss',
          callback: () => {
            this.$store.dispatch('setWalletDialog', {
              value,
              tab: 'purchase',
            });
          },
        });
      }

      return hasBalance && isBalanceSufficient;
    },

    async showIFrameDialog(url) {
      await this.$store.dispatch('showIFrameDialog', {
        url,
        title: 'Exit game',
        text: 'Are you sure you want to exit the game?',
        callback: () => {
          this.closeGameConfirm();
        },
      });
    },

    async updateIframeUrl(url) {
      try {
        const parentWindow = window.parent;
        if (parentWindow) {
          const iframe = parentWindow.document.getElementById('dialogFrameId');
          if (iframe) {
            iframe.src = url;
          }
        }
      } catch (e) {
        console.error('Error updating game url ', e);
      }
    },

    async closeGameConfirm() {
      let mqGameUrl = await this.$store.dispatch('getMqGameDomain');
      if (this.messageListener == null) {
        this.addMessageListener(mqGameUrl);
      }

      this.postMessage(mqGameUrl);
      this.showConfirmationDialog();
    },

    addMessageListener(mqGameUrl) {
      this.messageListener = window.addEventListener('message', this.handleMessageEvent.bind(this, mqGameUrl), false);
    },

    addSIDListener(mqGameUrl) {
      this.SIDListener = window.addEventListener('message', this.handleSIDEvent.bind(this, mqGameUrl), false);
      // TODO: temporary commented this fix before we figure out how it works
      // this.SIDListener = this.handleSIDEvent.bind(this, mqGameUrl);
      // window.addEventListener('message', this.SIDListener, false);
    },

    handleMessageEvent(mqGameUrl, e) {
      if (e.origin === mqGameUrl && e.data.sid) {
        this.SID = e.data.sid;
      }
    },

    handleSIDEvent(mqGameUrl, e) {
      if (e.origin === mqGameUrl && e.data.sid) {
        // TODO: temporary commented this fix before we figure out how it works
        // window.removeEventListener('message', this.SIDListener, false);
        this.SID = e.data.sid;
        const privateRoomId = this.$store.getters.getPrivateRoomID;
        this.$store.dispatch('saveGameSession', { sid: this.SID, privateRoomId: privateRoomId });
      }
    },

    postMessage(mqGameUrl) {
      try {
        window.frames.gameUrlFrame?.postMessage({ message: 'message' }, mqGameUrl);
      } catch (e) {
        console.error('post message error...', e);
      }
    },

    postSIDMessage(mqGameUrl) {
      try {
        window.frames.gameUrlFrame?.postMessage({ SID: 'sid' }, mqGameUrl);
      } catch (e) {
        console.error('post message sid error...', e);
      }
    },

    removeMessageListeners() {
      if (this.messageListener) {
        window.removeEventListener('message', this.messageListener, false);
        this.messageListener = null;
      }
      if (this.SIDListener) {
        window.removeEventListener('message', this.SIDListener, false);
        this.SIDListener = null;
      }
    },

    showConfirmationDialog() {
      const privateRoomId = this.$store.getters.getPrivateRoomID;
      this.$store.dispatch('showConfirmDialog', {
        title: 'Exiting game',
        text: 'Are you sure you want to exit the game?',
        callback: () => {
          this.removeMessageListeners();
          this.$store.dispatch('finishGameSession', { sid: this.SID, privateRoomId: privateRoomId });
          this.closeGame();
        },
      });
    },

    async closeGame(options = {}) {
      this.gameURL = '';
      const causedByBackButton = Boolean(options.causedByBackButton);
      await this.$store.dispatch('hideIFrameDialog', causedByBackButton);
      this.unsubscribeBrowserHistory(causedByBackButton);
      await this.$store.dispatch('sendGaEvent', {
        endpoint: '/endGame',
        body: {
          game_name: this.$store.getters.getGameById(this.gamedetail)?.NAME || this.gamedetail,
          currency: this.QCMode ? 'QC' : 'MC',
        },
      });
    },

    async enterPrivateRoom(roomId, homeURL, addFriendUrl, onFinish = () => {}) {
      if (!this.isAuthorized) {
        this.openSignUpDialog();

        onFinish && onFinish();
        return;
      }

      if (!roomId || !this.checkUserBalance()) {
        onFinish && onFinish();

        return;
      }

      await this.$store.commit('setPrivateRoomID', roomId);

      await this.$store.dispatch('requestGameplayTokenAs');
      await this.$store.dispatch('changeHomeUrl', homeURL);

      const token = this.$store.getters.getGameplayToken;
      const homeUrl = this.$store.getters.getHomeUrl;
      const mqGameUrl = await this.$store.dispatch('getMqGameDomain');

      const gameURL = new URL(process.env.VUE_APP_BATTLEGROUND_PRIVATE_GAMES_URL, mqGameUrl);
      const params = new URLSearchParams({
        homeUrl: homeUrl,
        mmcToken: token,
        mqcToken: token,
        privateRoomId: roomId,
        lang: 'en',
        addFriendUrl,
      });
      gameURL.search = params.toString();

      await this.showIFrameDialog(gameURL.toString());

      onFinish && onFinish();
    },

    handleGameClick(game) {
      if (!this.isAuthorized) {
        this.openSignUpDialog();
        return;
      }

      if (game.gameIdFF) {
        return this.launchGame(game.gameIdFF, 'FF', game.extendedParams ? 'extended' : undefined);
      }

      if (game.gameIdExtended) {
        return this.launchGame(game.gameIdExtended, 'PL', 'extended');
      }

      if (['PVP', 'FishTable'].includes(game.category)) {
        return this.$store.commit('SET_ROOM_BUYIN_POPUP_DATA', game.gameType);
      }

      if (game.redirect) {
        return this.$router.push(`/${game.redirect}`);
      }
    },
  },
};
